import * as joda from "@js-joda/core";
import * as c from "purify-ts/Codec";
import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import { makeEncoderFromCodec, makeRoute } from "trader.ts/route";
import { Source } from "trader.ts/types/source";
import { dyn, dynStyleVariadic, dynVariadic } from "trader.ts/ui";
import { checkAllCasesHandled } from "trader.ts/utils";
import {
  enrollment_status,
  enrollmentid,
  enrollmentrequest_status,
  enrollmentrequestid,
  formula_color,
  formula_type,
  formulaid,
  instrumentid,
  internalteacherid,
  lesson_location,
  lessongroupid,
  lessonid,
  periodid,
  planningmode,
  roomid,
  siteid,
  studentid,
  teacherid,
} from "../../../sql/classy/types";
import * as FA from "../../../styles/fontawesome.css.classes";
import * as planningstyle from "../../../styles/planning.css.classes";
import * as style from "../../../styles/style.css.classes";
import { makeClientsideRouter } from "../../client/clientsiderouter";
import {
  calendar_slot,
  renderWeekCalendar,
} from "../../client/renderweekcalendar";
import { country, formulaFull } from "../../domain/config";
import { holiday, period } from "../../domain/periods";
import {
  colors,
  formulaColorToPlanningColors,
  greyPlanningColors,
  paleBlue,
  whitePlanningColors,
} from "../../lib/colors";
import {
  getFromLocalStorage,
  localStorageSpec,
  setLocalStorage,
} from "../../lib/localstorage";
import { printStylePlanning } from "../../lib/printstyle";
import { timerangesOverlap } from "../../lib/time";
import {
  formulaColor,
  parseEnrollmentRequestStatus,
  parseFormulaColor,
  parseFormulaType,
  singleDueDateCalculation,
} from "../../parsing_from_ur";
import { renderLessonLocation } from "../../render";
import * as routes from "../../routes";
import { translateWeekday } from "../../translations_specific";
import { schoolurl } from "../../types";
import { planningDateKind } from "../planning_start_public/client";
import { renderAside, renderPlanningDateButtons } from "./aside";
import {
  calcSelectedElementDimensionsForCalendarSlot,
  getApproximateFloatingPopupHeightForPlanning,
  getFloatingPopupWidthForPlanning,
  renderFloatingPopup,
} from "./floatingpopup";
import { isSameSlot } from "./isSameSlot";
import {
  PlanningSelectedSlotDetails,
  renderSelectionAndForms,
} from "./selectionandforms";
import { isEqual } from "../../lib/isequal";
import { isStudentActiveAtLesson } from "../../lib/isstudentactiveatlesson";
import { match } from "ts-pattern";

const fullScreenLS: localStorageSpec<boolean> = {
  key: "classy-planning-fullscreen",
  codec: c.boolean,
};

export type planningClientMode =
  | { tag: "normal" }
  | {
      tag: "selected_existing_slot";
      weekday: joda.DayOfWeek;
      time: joda.LocalTime;
      internalteacherid: internalteacherid;
    }
  | {
      tag: "editing";
      weekday: joda.DayOfWeek;
      time: joda.LocalTime;
      internalteacherid: internalteacherid;
    }
  | {
      tag: "moving_enrollment";
      moving: {
        weekday: joda.DayOfWeek;
        time: joda.LocalTime;
        internalteacherid: internalteacherid;
        duration: number;
        lessongroupid: lessongroupid;
        student: {
          id: studentid;
          enrollmentid: enrollmentid;
          firstname: string;
          lastname: string;
        };
      };
    }
  | {
      tag: "moving_enrollment_selected";
      moving: {
        weekday: joda.DayOfWeek;
        time: joda.LocalTime;
        internalteacherid: internalteacherid;
        duration: number;
        lessongroupid: lessongroupid;
        student: {
          id: studentid;
          enrollmentid: enrollmentid;
          firstname: string;
          lastname: string;
        };
      };
      selected: {
        weekday: joda.DayOfWeek;
        time: joda.LocalTime;
        internalteacherid: internalteacherid;
        duration: number;
        lessongroupid: lessongroupid;
      };
    }
  | {
      tag: "moving";
      mode: "lesson" | "lessongroup";
      moving: {
        weekday: joda.DayOfWeek;
        time: joda.LocalTime;
        internalteacherid: internalteacherid;
        duration: number;
        lessongroupid: lessongroupid;
        lessonid: lessonid;
        formulaColor: formulaColor;
      };
    }
  | {
      tag: "moving_selected";
      mode: "lesson" | "lessongroup";
      moving: {
        weekday: joda.DayOfWeek;
        time: joda.LocalTime;
        internalteacherid: internalteacherid;
        duration: number;
        lessongroupid: lessongroupid;
        formulaColor: formulaColor;
      };
      selected: {
        weekday: joda.DayOfWeek;
        initial_time: joda.LocalTime;
        initial_duration: number;
        internalteacherid: internalteacherid;
      };
    }
  | { tag: "selecting_new"; subtype: newSubType }
  | {
      tag: "making_complement";
      weekday: joda.DayOfWeek;
      time: joda.LocalTime;
      duration: number;
      internalteacherid: internalteacherid;
      lessongroupid: lessongroupid;
    }
  | {
      tag: "selected_new";
      weekday: joda.DayOfWeek;
      initial_time: joda.LocalTime;
      initial_duration: number;
      internalteacherid: internalteacherid;
      subtype: newSubType;
    };

export type planning_slot = {
  internalteacherid: internalteacherid;
  weekday: joda.DayOfWeek;
  time: joda.LocalTime;
  duration: number;
  contents: planning_slot_contents;
};

export type planning_slot_contents_lesson = {
  tag: "lesson";
  complement:
    | {
        lessongroup: {
          id: lessongroupid;
          type: formula_type;
          location: lesson_location;
          roomId: roomid | null;
          description: string | null;
          lastLesson: joda.LocalDate | null;
          firstLesson: joda.LocalDate | null;
        };
        instrument: { id: instrumentid; description: string };
        formula: {
          type: formula_type;
          lessonduration: number;
          description: string;
          comments: string;
          color: formula_color;
        };
      }
    | "none_planned_yet"
    | "not_relevant";
  lessongroup: {
    id: lessongroupid;
    time: joda.LocalTime;
    type: formula_type;
    weekday: string;
    lessonduration: number;
    planningMode: planningmode;
    location: lesson_location;
    internalTeacherId: internalteacherid;
    roomId: roomid | null;
    description: string | null;
    firstLesson: joda.LocalDate | null;
    lastLesson: joda.LocalDate | null;
    startsinperiod: periodid;
    numberoflessons: number;
  };
  lesson: {
    id: lessonid;
    time: joda.LocalTime;
    date: joda.LocalDate;
  };
  teacherplanned: {
    id: teacherid;
    firstname: string;
    lastname: string;
  };
  teacheractual: {
    id: teacherid;
    internalteacherid: internalteacherid;
    firstname: string;
    lastname: string;
  };
  formula: {
    id: formulaid;
    description: string;
    color: formulaColor;
    numberoflessons: number;
  };
  instrument: { id: instrumentid; description: string };
  room: {
    description: string | null;
    siteid: siteid | null;
  };
  students: {
    enrollmentid: enrollmentid;
    id: studentid;
    firstname: string;
    lastname: string;
    enrollment_status: enrollment_status;
    enrollment_startedlessonson: joda.LocalDate;
    enrollment_stoppedlessonson: joda.LocalDate | null;
    enrollment_paid: boolean;
    enrollment_has_unpaid_nonperiodic_overdue_eps: boolean;
    student_period_fee_paid: boolean;
    student_period_fee_has_unpaid_nonperiodic_overdue_eps: boolean;
  }[];
  enrollmentrequests: {
    enrollmentrequestid: enrollmentrequestid;
    status: enrollmentrequest_status;
    id: studentid;
    firstname: string;
    lastname: string;
  }[];
};

export type planning_slot_contents_enrollmentrequest = {
  tag: "enrollmentrequest";
  enrollmentrequest: {
    id: enrollmentrequestid;
    status: enrollmentrequest_status;
    weekday: string;
    time: joda.LocalTime;
    reserveduntil: null | joda.LocalDate;
  };
  student: {
    id: studentid;
    firstname: string;
    lastname: string;
  };
  teacher: {
    id: teacherid;
    internalteacherid: internalteacherid;
    firstname: string;
    lastname: string;
  };
  instrument: {
    id: instrumentid;
    description: string;
  };
  formula: {
    type: string;
    lessonduration: number;
    description: string;
    comments: string;
    color: string;
  };
};

export type planning_slot_teacher_absence = {
  tag: "teacher_absence";
  teacher: {
    internalteacherid: internalteacherid;
    firstname: string;
    lastname: string;
  };
};

export type planning_slot_teaching_period = {
  tag: "teaching_period";
  teacher: {
    firstname: string;
    lastname: string;
  };
  firstday: joda.LocalDate | null;
  location: "NotSpecifiedS" | "OnlineS" | "HomeS" | "RoomS";
  room: {
    id: roomid | null;
    description: string | null;
  };
};

export type planning_slot_contents =
  | planning_slot_contents_lesson
  | planning_slot_contents_enrollmentrequest
  | planning_slot_teacher_absence
  | planning_slot_teaching_period;

export type planningDisplayMode =
  | "formula"
  | "room"
  | "names"
  | "instrument"
  | "comment"
  | "teacher";

type newSubType = "private" | "group" | "free" | "absence" | "camp";

const modeC = routes.magicCodec<planningClientMode>();
const modeRoute = makeRoute("/mode/{mode:mode}", {
  mode: makeEncoderFromCodec(modeC),
});

const router = makeClientsideRouter(modeRoute, { tag: "normal" });

export type planningFilter =
  | { tag: "teachers"; ids: internalteacherid[] }
  | { tag: "instruments"; ids: instrumentid[] };

export type planningClientOptions = {
  schoolhours: [joda.LocalTime, joda.LocalTime];
  country: country;
  schoolurl: schoolurl;
  days: { date: joda.LocalDate; kind: planningDateKind }[];
  initialSlot: [joda.DayOfWeek, joda.LocalTime] | null;
  initialSlots: planning_slot[];
  initialFilter: planningFilter;
  initialDisplayMode: planningDisplayMode[];
  allInternalTeachers: {
    id: teacherid;
    internalteacherid: internalteacherid;
    firstname: string;
    lastname: string;
    instruments: { id: instrumentid; description: string }[];
    active: boolean;
    absences: {
      date: joda.LocalDate;
      time: joda.LocalTime;
      duration: number;
    }[];
  }[];
  allInstruments: {
    id: instrumentid;
    description: string;
  }[];
  allFormulas: formulaFull[];
  period: period & { holidays: holiday[] };
  nextPeriod: null | (period & { holidays: holiday[] });
  teacherMode: { teacherid: teacherid; onlyEditExistingLgs: boolean } | null;
  each_period_fee_due_date: singleDueDateCalculation;
  each_period_fee_pay_during_online_enrollment: boolean;
};

export function render(
  opts: planningClientOptions,
): HTMLElement | HTMLElement[] {
  setTimeout(() => {
    const selected = document.querySelector("." + planningstyle.selected);
    if (selected) {
      selected.scrollIntoView(false);
    }
  }, 40);

  const hours: [number, number] = [
    opts.schoolhours[0].hour(),
    opts.schoolhours[1].hour() + (opts.schoolhours[1].minute() > 0 ? 1 : 0),
  ];
  const slotsS: Source<planning_slot[]> = new Source(opts.initialSlots);
  const today = joda.LocalDate.now();
  const fullScreenS = new Source(getFromLocalStorage(fullScreenLS) || false);

  const modeS: Source<planningClientMode> = router.start();
  const initialSelectedSlot =
    opts.initialSlot === null
      ? null
      : opts.initialSlots.find(
          (sl) =>
            isEqual(opts.initialSlot![1], sl.time) &&
            isEqual(opts.initialSlot![0], sl.weekday),
        );
  if (initialSelectedSlot) {
    document.location.hash = router.makeHashLink({
      tag: "selected_existing_slot",
      ...initialSelectedSlot,
    });
  }
  const displayModeS: Source<planningDisplayMode[]> = new Source(
    opts.initialDisplayMode,
  );
  const filterS: Source<planningFilter> = new Source(opts.initialFilter);

  async function reloadSlots() {
    const currentFilter = filterS.get();
    const teachersfilter =
      currentFilter.tag === "teachers"
        ? currentFilter.ids
        : opts.allInternalTeachers
            .filter(
              (t) =>
                t.active === true &&
                t.instruments.some((instr) =>
                  currentFilter.ids.includes(instr.id),
                ),
            )
            .map((t) => t.internalteacherid);
    const newSlots = await rpc(routes.loadSlotsSpec, undefined, {
      days: opts.days,
      teachersfilter,
    });
    slotsS.set(newSlots);
  }

  const aside = renderAside(
    () => {
      void reloadSlots();
      document.location.hash = router.makeHashLink({ tag: "normal" });
    },
    opts.allInternalTeachers.filter((t) => t.active === true),
    opts.allInstruments,
    opts.days,
    opts.period,
    displayModeS,
    filterS,
    opts.teacherMode,
  );
  // Can't keep this inside mode, as mode needs to be serializable
  const currMode = modeS.get();
  const newSlotTimeS = new Source(
    currMode.tag === "selected_new"
      ? currMode.initial_time
      : currMode.tag === "moving_selected"
      ? currMode.selected.initial_time
      : joda.LocalTime.of(8),
  );
  const newSlotDurationS = new Source(
    currMode.tag === "selected_new"
      ? currMode.initial_duration
      : currMode.tag === "moving_selected"
      ? currMode.selected.initial_duration
      : 30,
  );

  const calendar = renderCalendar(
    slotsS,
    filterS,
    displayModeS,
    modeS,
    newSlotTimeS,
    newSlotDurationS,
    hours,
    opts,
    today,
  );

  function setFullScreen(b: boolean) {
    fullScreenS.set(b);
    setLocalStorage(fullScreenLS, b);
  }

  const newButtons =
    opts.teacherMode && opts.teacherMode.onlyEditExistingLgs
      ? []
      : renderNewButtons(
          modeS,
          fullScreenS,
          setFullScreen,
          opts.allFormulas,
          opts.period,
          opts.days,
        );

  const popup = renderFloatingPopup(
    modeS,
    slotsS,
    null,
    function findSlot(
      mode: planningClientMode,
      slots: planning_slot[],
    ): PlanningSelectedSlotDetails | null {
      if (
        mode.tag === "normal" ||
        mode.tag === "selecting_new" ||
        mode.tag === "moving" ||
        mode.tag === "moving_enrollment"
      ) {
        return null;
      } else if (mode.tag === "selected_existing_slot") {
        const slot = slots.find((s) => isSameSlot(s, mode));
        return (slot && { tag: mode.tag, mode: mode, slot }) || null;
      } else if (mode.tag === "making_complement") {
        const slot = slots.find((s) => isSameSlot(s, mode));
        return (slot && { tag: mode.tag, mode: mode, slot }) || null;
      } else if (mode.tag === "moving_enrollment_selected") {
        const slot = slots.find((s) => isSameSlot(s, mode.selected));
        return (slot && { tag: mode.tag, mode: mode, slot }) || null;
      } else if (mode.tag === "editing") {
        const slot = slots.find((s) => isSameSlot(s, mode));
        return (slot && { tag: mode.tag, mode: mode, slot }) || null;
      } else if (mode.tag === "selected_new") {
        return { tag: mode.tag, mode };
      } else if (mode.tag === "moving_selected") {
        return { tag: mode.tag, mode };
      } else {
        return checkAllCasesHandled(mode);
      }
    },
    {
      getSelectedElementDimensions: function (
        found: PlanningSelectedSlotDetails,
      ) {
        const weekdayTimeDuration = (() => {
          if (
            found.tag === "selected_existing_slot" ||
            found.tag === "making_complement" ||
            found.tag === "moving_enrollment_selected" ||
            found.tag === "editing"
          ) {
            return found.slot;
          } else if (found.tag === "selected_new") {
            return {
              weekday: found.mode.weekday,
              time: found.mode.initial_time,
              duration: found.mode.initial_duration,
            };
          } else if (found.tag === "moving_selected") {
            return {
              weekday: found.mode.selected.weekday,
              time: found.mode.selected.initial_time,
              duration: found.mode.selected.initial_duration,
            };
          } else {
            return checkAllCasesHandled(found);
          }
        })();
        return calcSelectedElementDimensionsForCalendarSlot(
          hours[0],
          weekdayTimeDuration,
        );
      },
      calcPopupWidth: getFloatingPopupWidthForPlanning,
      getApproximateFloatingPopupHeight:
        getApproximateFloatingPopupHeightForPlanning,
      renderPopupContents: function (
        details,
        slots: planning_slot[],
      ): HTMLElement | HTMLElement[] {
        return renderSelectionAndForms(
          opts,
          slots,
          { timeS: newSlotTimeS, durationS: newSlotDurationS },
          async (sl) => {
            await reloadSlots();
            document.location.hash = router.makeHashLink(
              sl ? { tag: "selected_existing_slot", ...sl } : { tag: "normal" },
            );
          },
          (m) => (document.location.hash = router.makeHashLink(m)),
          details,
        );
      },
      modeIsDayView: () => false,
      offsetRight: 8,
      offsetLeft: 8 + 35, // time indicators
    },
  );

  const floatingPopupHeightS = new Source(0);
  window.addEventListener("resize", () =>
    floatingPopupHeightS.set(floatingPopupHeightS.get()),
  );

  window.addEventListener("keyup", (ev) => {
    const modeT = modeS.get().tag;
    if (
      ev.key === "Escape" &&
      (modeT === "moving" ||
        modeT === "moving_selected" ||
        modeT === "moving_enrollment" ||
        modeT === "selecting_new")
    ) {
      document.location.hash = router.makeHashLink({ tag: "normal" });
    }
    if (modeT === "normal" && ev.key === "b") {
      setFullScreen(!fullScreenS.get());
    }
  });

  return [
    dynStyleVariadic(
      [modeS, fullScreenS],
      ([mode, fullScreen]) => {
        // We disable scrolling, as popup is positioned fixed
        const modeStyle =
          mode.tag === "selected_existing_slot" || mode.tag === "selected_new"
            ? "overflow: hidden;"
            : "";
        const fullScreenStyle = fullScreen
          ? "position: absolute; z-index: 100; background-color: white; width: 100vw; min-height: var(--viewportminusnav);"
          : "";
        return modeStyle + fullScreenStyle;
      },
      <main
        style={`
  max-height: var(--viewportminusnav);
  overflow-y: auto;
  max-width: unset;
`}
      >
        {newButtons}
        {calendar}
        {popup}
        {printStylePlanning()}
      </main>,
    ),
    aside,
  ];
}

function renderNewButtons(
  modeS: Source<planningClientMode>,
  fullScreenS: Source<boolean>,
  setFullScreen: (b: boolean) => void,
  allFormulas: formulaFull[],
  period: planningClientOptions["period"],
  days: planningClientOptions["days"],
) {
  const cancelButton = dyn(modeS, function (mode) {
    const cancelButtonLabel: string | null = (() => {
      if (mode.tag === "moving") {
        return "Annuleer verplaatsing";
      } else if (mode.tag === "moving_enrollment") {
        return "Annuleer verplaatsing";
      } else if (mode.tag === "selecting_new") {
        return "Terug";
      } else if (
        mode.tag === "normal" ||
        mode.tag === "moving_selected" ||
        mode.tag === "moving_enrollment_selected" ||
        mode.tag === "selected_existing_slot" ||
        mode.tag === "selected_new" ||
        mode.tag === "making_complement" ||
        mode.tag === "editing"
      ) {
        return null;
      } else {
        return checkAllCasesHandled(mode);
      }
    })();

    return cancelButtonLabel !== null ? (
      <a
        style="margin-right: 8px"
        href={router.makeHashLink({ tag: "normal" })}
      >
        <button className={style.button_accent}>
          <i className={FA.icon_undo_solid}></i>
          {cancelButtonLabel}
        </button>
      </a>
    ) : (
      <span></span>
    );
  });

  function renderNewButton(
    subtype: "private" | "group" | "camp" | "absence" | "free",
    icon: string,
  ): HTMLElement {
    return (
      <a
        style="margin-right: 8px"
        href={router.makeHashLink({
          tag: "selecting_new",
          subtype: subtype,
        })}
      >
        <button>
          <i className={icon}></i>
          {renderNewSubtype(subtype)}
        </button>
      </a>
    );
  }

  return (
    <div style="display: flex">
      <div>
        {dyn(fullScreenS, (fullScreen) => {
          const classes =
            style.button_has_only_icon +
            style.button_accent +
            style.button_round;
          const st = `font-size: 1.1rem;
                      position: relative;
                      left: -1px;`;
          return fullScreen ? (
            <button
              title={"Verkleinen" + " (b)"}
              onclick={() => setFullScreen(false)}
              className={classes}
            >
              <i className={FA.icon_compress} style={st}></i>
            </button>
          ) : (
            <button
              title={"Vergroten" + " (b)"}
              onclick={() => setFullScreen(true)}
              className={classes}
            >
              <i className={FA.icon_expand} style={st}></i>
            </button>
          );
        })}
      </div>
      <div style="flex: 1"></div>
      <div>
        {cancelButton}
        {renderNewButton("private", FA.icon_user_solid)}
        {allFormulas.some((f) => f.type_.tag === "Group")
          ? renderNewButton("group", FA.icon_family)
          : []}
        {allFormulas.some((f) => f.type_.tag === "Camp")
          ? renderNewButton("camp", FA.icon_family)
          : []}
        {renderNewButton("absence", FA.icon_house)}
        {renderNewButton("free", FA.icon_calendar_alt_regular)}
      </div>
      {dyn(fullScreenS, (fullScreen) => {
        // So we can still navigate dates even if we're in fullscreen mode
        if (fullScreen === true) {
          const buttons = renderPlanningDateButtons(
            period,
            days.first()!.date,
            days.last()!.date,
            (d) =>
              routes.planningRoute.link({
                teacher: undefined,
                time: undefined,
                date: d || undefined,
              }),
          );
          return (
            <div style="width: calc(var(--leftbarwidth) - 48px); margin-left: 64px; margin-bottom: -48px;">
              {buttons}
            </div>
          );
        } else {
          return [];
        }
      })}
    </div>
  );
}

function renderCalendar(
  slotsS: Source<planning_slot[]>,
  filterS: Source<planningFilter>,
  displayModeS: Source<planningDisplayMode[]>,
  modeS: Source<planningClientMode>,
  newSlotTimeS: Source<joda.LocalTime>,
  newSlotDurationS: Source<number>,
  hours: [number, number],
  opts: planningClientOptions,
  today: joda.LocalDate,
) {
  return dynVariadic(
    [slotsS, displayModeS, modeS, filterS],
    function renderSlots([slots, displayMode, mode, currentFilter]) {
      const slotsToRender = slots.map((sl) => {
        const colors =
          sl.contents.tag === "teacher_absence"
            ? greyPlanningColors
            : sl.contents.tag === "teaching_period"
            ? paleBlue
            : sl.contents.tag === "enrollmentrequest"
            ? whitePlanningColors
            : sl.contents.tag === "lesson"
            ? ((): colors | [colors, colors] => {
                const c = sl.contents;
                const primaryColor = formulaColorToPlanningColors(
                  c.formula.color,
                );
                return sl.contents.complement === "not_relevant"
                  ? primaryColor
                  : sl.contents.complement === "none_planned_yet"
                  ? [primaryColor, paleBlue]
                  : (sl.contents.lessongroup.firstLesson || today).isBefore(
                      sl.contents.complement.lessongroup.firstLesson || today,
                    )
                  ? [
                      primaryColor,
                      formulaColorToPlanningColors(
                        parseFormulaColor(sl.contents.complement.formula.color),
                      ),
                    ]
                  : [
                      formulaColorToPlanningColors(
                        parseFormulaColor(sl.contents.complement.formula.color),
                      ),
                      primaryColor,
                    ];
              })()
            : checkAllCasesHandled(sl.contents);

        const slotIsBeingMoved =
          sl.contents.tag === "lesson" &&
          (mode.tag === "moving" ||
            mode.tag === "moving_selected" ||
            mode.tag === "moving_enrollment" ||
            mode.tag === "moving_enrollment_selected") &&
          sl.contents.lessongroup.id === mode.moving.lessongroupid;

        const selectedClass =
          (mode.tag === "editing" && isSameSlot(sl, mode)) ||
          (mode.tag === "selected_existing_slot" && isSameSlot(sl, mode)) ||
          (mode.tag === "making_complement" && isSameSlot(sl, mode)) ||
          slotIsBeingMoved ||
          (mode.tag === "moving_enrollment_selected" &&
            isSameSlot(sl, mode.selected))
            ? planningstyle.selected
            : "";

        // If we have a selection, but this slot is not selected -> seethrough
        const notSelectedClass =
          (mode.tag === "selected_existing_slot" ||
            mode.tag == "editing" ||
            mode.tag === "making_complement" ||
            slotIsBeingMoved ||
            mode.tag === "moving_enrollment_selected") &&
          !selectedClass
            ? style.seethrough
            : "";

        const movingEnrollmentSeethroughClass =
          mode.tag === "moving_enrollment" &&
          (sl.contents.tag !== "lesson" ||
            parseFormulaType(sl.contents.lessongroup.type).tag === "Private")
            ? style.seethrough
            : "";

        const absenceClass =
          sl.contents.tag === "teacher_absence"
            ? planningstyle.slot_absence
            : "";

        const lastLessonIndicator =
          sl.contents.tag === "lesson" &&
          isEqual(
            sl.contents.lesson.date,
            sl.contents.lessongroup.lastLesson,
          ) ? (
            <div style="font-size: 18px; line-height: 0.85rem; position: relative; top: 3px;">
              *
            </div>
          ) : (
            []
          );

        const type_ = (sl.contents.tag === "lesson" &&
          parseFormulaType(sl.contents.lessongroup.type)) || { tag: "Private" };
        const contents = sl.contents;
        const enrollmentsAndReservations =
          contents.tag === "lesson"
            ? contents.students.filter((s) =>
                isStudentActiveAtLesson(contents.lesson.date, s),
              ).length +
              contents.enrollmentrequests.filter(
                (er) => er.status === "Reserved",
              ).length
            : 0;

        const numberOfStudentsIndicator =
          contents.tag === "lesson" &&
          (type_.tag === "Group" || type_.tag === "Camp") &&
          contents.students.filter((s) =>
            isStudentActiveAtLesson(contents.lesson.date, s),
          ).length > 0 ? (
            <div>
              {enrollmentsAndReservations +
                "/" +
                (type_.tag === "Group"
                  ? type_.maxnumberofstudents
                  : type_.maxnumberofstudentscamp)}
            </div>
          ) : (
            []
          );
        const unpaidIndicator =
          sl.contents.tag === "lesson" &&
          sl.contents.students.some(
            (stu) =>
              stu.enrollment_paid === false ||
              stu.student_period_fee_paid === false,
          ) ? (
            <div
              style={
                sl.contents.students.some(
                  (stu) =>
                    stu.enrollment_has_unpaid_nonperiodic_overdue_eps ||
                    stu.student_period_fee_has_unpaid_nonperiodic_overdue_eps,
                )
                  ? "color: red; font-weight: 600;"
                  : ""
              }
            >
              €
            </div>
          ) : (
            []
          );

        const slotToRender: calendar_slot = {
          ...sl,
          contents: (
            <div>
              {slotIsBeingMoved
                ? "Wordt verplaatst"
                : calcSlotTextContents(displayMode, sl.contents)}
              <div className={planningstyle.indicators}>
                {unpaidIndicator}
                {numberOfStudentsIndicator}
                {lastLessonIndicator}
              </div>
            </div>
          ),
          colors,
          className:
            selectedClass +
            movingEnrollmentSeethroughClass +
            notSelectedClass +
            absenceClass,
          onclick:
            mode.tag === "selecting_new" || mode.tag === "moving"
              ? null
              : mode.tag === "moving_enrollment"
              ? sl.contents.tag === "lesson" &&
                (parseFormulaType(sl.contents.lessongroup.type).tag ===
                  "Group" ||
                  parseFormulaType(sl.contents.lessongroup.type).tag === "Camp")
                ? {
                    href: router.makeHashLink({
                      tag: "moving_enrollment_selected",
                      moving: mode.moving,
                      selected: {
                        weekday: sl.weekday,
                        time: sl.time,
                        internalteacherid: sl.internalteacherid,
                        duration: sl.duration,
                        lessongroupid: sl.contents.lessongroup.id,
                      },
                    }),
                  }
                : null
              : mode.tag === "selected_existing_slot" ||
                mode.tag === "normal" ||
                mode.tag === "editing" ||
                mode.tag === "moving_selected" ||
                mode.tag === "moving_enrollment_selected" ||
                mode.tag === "making_complement" ||
                mode.tag === "selected_new"
              ? {
                  href: router.makeHashLink({
                    tag: "selected_existing_slot",
                    ...sl,
                  }),
                }
              : checkAllCasesHandled(mode),
        };
        return slotToRender;
      });

      const newPreviewSlot: null | calendar_slot =
        mode.tag === "normal" ||
        mode.tag === "selecting_new" ||
        mode.tag === "editing" ||
        mode.tag === "selected_existing_slot" ||
        mode.tag === "making_complement" ||
        mode.tag === "moving_enrollment" ||
        mode.tag === "moving_enrollment_selected" ||
        mode.tag === "moving"
          ? null
          : mode.tag === "selected_new"
          ? {
              internalteacherid: mode.internalteacherid,
              time: newSlotTimeS,
              duration: newSlotDurationS,
              weekday: mode.weekday,
              contents: renderNewSubtype(mode.subtype),
              colors: {
                primary: "",
                lighter: "",
                darker: "",
                text: "",
              },
              onclick: null,
              className: planningstyle.editblock + planningstyle.selected,
            }
          : mode.tag === "moving_selected"
          ? {
              internalteacherid: mode.selected.internalteacherid,
              time: newSlotTimeS,
              duration: newSlotDurationS,
              weekday: mode.selected.weekday,
              contents: "",
              colors: formulaColorToPlanningColors(mode.moving.formulaColor),
              onclick: null,
              className: planningstyle.editblock + planningstyle.selected,
            }
          : checkAllCasesHandled(mode);

      const teachersShowing = opts.allInternalTeachers.filter((t) =>
        currentFilter.tag === "teachers"
          ? currentFilter.ids.includes(t.internalteacherid)
          : t.active &&
            t.instruments.some((instr) => currentFilter.ids.includes(instr.id)),
      );

      return renderWeekCalendar({
        hours,
        groupByTeachers: teachersShowing,
        alwaysShowGroups: currentFilter.tag === "instruments",
        collapseEmptyTeachers:
          mode.tag === "moving" || mode.tag === "selecting_new" ? false : true,
        weekdays: opts.days.map((d) => [
          d.date.dayOfWeek(),
          {
            label: d.date.dayOfMonth().toString(),
            sublabel: translateWeekday(d.date.dayOfWeek()).slice(0, 2),
            contents:
              d.kind.tag === "holiday"
                ? { tag: "holiday", description: d.kind.description }
                : d.kind.tag === "outsideperiod"
                ? { tag: "past" }
                : d.kind.tag === "open"
                ? d.date.isBefore(today)
                  ? { tag: "past" }
                  : d.date.isEqual(today)
                  ? { tag: "today" }
                  : null
                : checkAllCasesHandled(d.kind),
          },
        ]),
        slots: slotsToRender.concat(newPreviewSlot || []),
        onClickOutsideSlot: function (mousePos) {
          if (mode.tag === "normal" || mode.tag === "moving_enrollment") {
            return;
          } else if (
            mode.tag === "editing" ||
            mode.tag === "making_complement" ||
            mode.tag === "selected_existing_slot" ||
            mode.tag === "moving_selected" ||
            mode.tag === "moving_enrollment_selected" ||
            mode.tag === "selected_new"
          ) {
            location.hash = router.makeHashLink({ tag: "normal" });
          } else if (mode.tag === "moving") {
            const previewSlot = calcPreviewSlot(
              opts.allFormulas,
              slots,
              mode,
              mousePos,
            );
            if (previewSlot) {
              newSlotTimeS.set(previewSlot.time);
              newSlotDurationS.set(previewSlot.duration);
              location.hash = router.makeHashLink({
                tag: "moving_selected",
                mode: mode.mode,
                moving: mode.moving,
                selected: {
                  initial_time: previewSlot.time,
                  initial_duration: previewSlot.duration,
                  weekday: previewSlot.weekday,
                  internalteacherid: mousePos.internalteacherid,
                },
              });
              return;
            } else {
              return;
            }
          } else if (mode.tag === "selecting_new") {
            const previewSlot = calcPreviewSlot(
              opts.allFormulas,
              slots,
              mode,
              mousePos,
            );
            if (previewSlot) {
              newSlotTimeS.set(previewSlot.time);
              newSlotDurationS.set(previewSlot.duration);
              location.hash = router.makeHashLink({
                tag: "selected_new",
                initial_time: previewSlot.time,
                initial_duration: previewSlot.duration,
                subtype: mode.subtype,
                weekday: previewSlot.weekday,
                internalteacherid: mousePos.internalteacherid,
              });
              return;
            } else {
              return;
            }
          } else {
            return checkAllCasesHandled(mode);
          }
        },
        onMouseMove: function (mousePos, showPreviewSlot) {
          const previewSlot = calcPreviewSlot(
            opts.allFormulas,
            slots,
            mode,
            mousePos,
          );

          if (previewSlot) {
            showPreviewSlot({
              ...previewSlot,
              contents: previewSlot.label,
              colors: previewSlot.formulaColor
                ? formulaColorToPlanningColors(previewSlot.formulaColor)
                : {
                    primary: "",
                    lighter: "",
                    darker: "",
                    text: "",
                  },
              onclick: null,
              className: planningstyle.editblock,
            });
          }
        },
      });
    },
  );
}

function calcSlotTextContents(
  displayMode: planningDisplayMode[],
  c: planning_slot_contents,
): string {
  if (c.tag === "teacher_absence") {
    return "Docent afwezig";
  } else if (c.tag === "teaching_period") {
    return "Vrij";
  } else if (c.tag === "enrollmentrequest") {
    const studentname = c.student.firstname + " " + c.student.lastname;
    const status = parseEnrollmentRequestStatus(c.enrollmentrequest.status);
    if (status === "Reserved") {
      return "Reservatie" + ": " + studentname;
    } else if (status === "InvitationER") {
      return "Uitnodiging" + ": " + studentname;
    } else {
      return "Aanvraag" + ": " + studentname;
    }
  } else if (c.tag === "lesson") {
    return displayMode.map((dm) => renderForDisplayMode(c, dm)).join(" - ");
  } else {
    return checkAllCasesHandled(c);
  }

  function renderForDisplayMode(
    c: planning_slot_contents_lesson,
    displayMode: planningDisplayMode,
  ): string {
    if (displayMode === "formula") {
      return c.formula.description;
    } else if (displayMode === "instrument") {
      return c.instrument.description;
    } else if (displayMode === "room") {
      return renderLessonLocation(
        c.lessongroup.location,
        c.room.description || null,
      );
    } else if (displayMode === "comment") {
      return c.lessongroup.description || "";
    } else if (displayMode === "teacher") {
      return c.teacheractual.firstname + " " + c.teacheractual.lastname;
    } else if (displayMode === "names") {
      return c.students
        .filter((s) => isStudentActiveAtLesson(c.lesson.date, s))
        .map((s) => s.firstname)
        .concat(
          // only shown while reservation is still valid or showonplanninguntil
          c.enrollmentrequests.map(
            (req) =>
              req.firstname +
              match(parseEnrollmentRequestStatus(req.status))
                .with("Reserved", () => "*")
                .with("InvitationER", () => "*")
                .otherwise(() => ""),
          ),
        )
        .join(", ");
    } else {
      return checkAllCasesHandled(displayMode);
    }
  }
}

function anchorTime(t: joda.LocalTime, _duration: number): joda.LocalTime {
  const duration = _duration % 60;
  if (duration === 60) {
    return joda.LocalTime.of(t.hour(), 0);
  } else if (duration === 30) {
    return t.minute() > 30
      ? joda.LocalTime.of(t.hour(), 30)
      : joda.LocalTime.of(t.hour(), 0);
  } else if (duration === 20) {
    return t.minute() > 40
      ? joda.LocalTime.of(t.hour(), 40)
      : t.minute() > 20
      ? joda.LocalTime.of(t.hour(), 20)
      : joda.LocalTime.of(t.hour(), 0);
  } else if (duration === 15 || duration === 45) {
    return t.minute() > 45
      ? joda.LocalTime.of(t.hour(), 45)
      : t.minute() > 30
      ? joda.LocalTime.of(t.hour(), 30)
      : t.minute() > 15
      ? joda.LocalTime.of(t.hour(), 15)
      : joda.LocalTime.of(t.hour(), 0);
  } else {
    return anchorTime(t, 30);
  }
}

function calcPreviewSlotDuration(
  allFormulas: formulaFull[],
  subtype: newSubType,
): number {
  return subtype === "private"
    ? allFormulas
        .filter((f) => f.type_.tag === "Private")
        .map((f) => f.lessonduration)
        .minBy((s) => s) || 30
    : subtype === "group"
    ? allFormulas
        .filter((f) => f.type_.tag === "Group")
        .map((f) => f.lessonduration)
        .minBy((s) => s) || 30
    : subtype === "camp"
    ? allFormulas
        .filter((f) => f.type_.tag === "Camp")
        .map((f) => f.lessonduration)
        .minBy((s) => s) || 240
    : subtype === "free"
    ? 30
    : subtype === "absence"
    ? 60
    : checkAllCasesHandled(subtype);
}

function calcPreviewSlot(
  allFormulas: formulaFull[],
  slots: planning_slot[],
  mode: planningClientMode,
  {
    weekday,
    time,
    internalteacherid,
  }: {
    weekday: joda.DayOfWeek;
    time: joda.LocalTime;
    internalteacherid: internalteacherid;
  },
): null | {
  internalteacherid: internalteacherid;
  duration: number;
  time: joda.LocalTime;
  label: string;
  weekday: joda.DayOfWeek;
  formulaColor: null | formulaColor;
} {
  if (mode.tag === "selecting_new" || mode.tag === "moving") {
    const formulaColor =
      mode.tag === "selecting_new" ? null : mode.moving.formulaColor;
    const previewSlotDuration =
      mode.tag === "selecting_new"
        ? calcPreviewSlotDuration(allFormulas, mode.subtype)
        : mode.moving.duration;
    const label = mode.tag === "moving" ? "" : renderNewSubtype(mode.subtype);
    const previewSlotTime = anchorTime(time, previewSlotDuration);
    if (
      mode.tag === "moving" ||
      mode.subtype === "private" ||
      mode.subtype === "group"
    ) {
      const possibleOverlappers = slots.filter(
        (s) =>
          s.internalteacherid === internalteacherid &&
          ((s.contents.tag === "lesson" &&
            // ignoring the selected lessongroup as we can plan over it
            s.contents.lessongroup.id !==
              (mode.tag === "moving" ? mode.moving.lessongroupid : -1)) ||
            s.contents.tag === "enrollmentrequest" ||
            s.contents.tag === "teacher_absence"),
      );
      const overlappingExistingSlot = possibleOverlappers.find((s) =>
        timerangesOverlap(s, {
          duration: previewSlotDuration,
          time: previewSlotTime,
          weekday,
        }),
      );
      if (overlappingExistingSlot) {
        // Gonna try to push preview slot after this overlapping slot!
        const overlappingExistingSlotEnd =
          overlappingExistingSlot.time.plusMinutes(
            overlappingExistingSlot.duration,
          );
        if (
          overlappingExistingSlotEnd.isAfter(
            previewSlotTime.plusMinutes(previewSlotDuration),
          )
        ) {
          // preview slot fully overlapped
          return null;
        } else {
          // slot only overlaps partially
          if (
            !possibleOverlappers.some(
              (sl) =>
                isEqual(sl.weekday, weekday) &&
                sl.time.isAfter(overlappingExistingSlot.time) &&
                timerangesOverlap(sl, {
                  duration: previewSlotDuration,
                  time: overlappingExistingSlotEnd,
                  weekday,
                }),
            )
          ) {
            // after this slot there is space -> show that
            return {
              internalteacherid,
              duration: previewSlotDuration,
              time: overlappingExistingSlotEnd,
              weekday,
              label,
              formulaColor,
            };
          } else {
            // Still no space -> nothing
            return null;
          }
        }
      }
    }

    if (
      mode.tag === "selecting_new" &&
      mode.subtype === "absence" &&
      slots.some(
        (s) =>
          s.internalteacherid === internalteacherid &&
          s.contents.tag === "teacher_absence" &&
          timerangesOverlap(s, {
            duration: previewSlotDuration,
            time: previewSlotTime,
            weekday,
          }),
      )
    ) {
      return null;
    }
    return {
      internalteacherid,
      duration: previewSlotDuration,
      time: previewSlotTime,
      weekday,
      label,
      formulaColor,
    };
  } else {
    return null;
  }
}

function renderNewSubtype(subtype: newSubType): string {
  switch (subtype) {
    case "private":
      return "Privéles";
    case "group":
      return "Groepsles";
    case "camp":
      return "Kamp";
    case "absence":
      return "Afwezig";
    case "free":
      return "Vrij";
    default:
      return checkAllCasesHandled(subtype);
  }
}
